import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, alpha } from '@mui/material';
import { FEATURES, getHealth, pct } from '../../utils/platformKpi';
import { Section, fmtRate } from './common';

const LOW_ADOPTION = 30; // % — below this a feature header is tinted as a product question.
const cellSx = { fontSize: '0.75rem', py: 0.5, px: 1, whiteSpace: 'nowrap' };
const HEALTH_RANK = { red: 0, amber: 1, green: 2 };

/** Centers × core features, used in the last 30 days (design §3.6). */
const AdoptionHeatmap = ({ centers, source, onOpen }) => {
  // Same order as the centers table: worst health first, then name.
  const rows = [...centers].sort((a, b) => HEALTH_RANK[getHealth(a).level] - HEALTH_RANK[getHealth(b).level]
    || (a.name || '').localeCompare(b.name || ''));
  const usedBy = Object.fromEntries(FEATURES.map((f) => [f.key, centers.filter((c) => c.features?.[f.key]).length]));

  return (
    <Section title="Feature adoption (30 days)" source={source}>
      <TableContainer sx={{ overflowX: 'auto', maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Center</TableCell>
              {FEATURES.map((f) => {
                const low = centers.length > 0 && pct(usedBy[f.key], centers.length) < LOW_ADOPTION;
                return (
                  <TableCell key={f.key} align="center"
                    sx={{ ...cellSx, fontWeight: 600, bgcolor: low ? (t) => alpha(t.palette.warning.main, 0.15) : undefined }}>
                    {low ? <Tooltip title="Few centers use this — fix it, explain it better, or drop it"><span>{f.label}</span></Tooltip> : f.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpen(c)}>
                <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                  <Tooltip title={c.name || ''} placement="right">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{c.name}</Typography>
                      <Typography noWrap sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{c.code}</Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                {FEATURES.map((f) => (
                  <TableCell key={f.key} align="center" sx={cellSx}>
                    <Box sx={{
                      width: 12, height: 12, borderRadius: '50%', mx: 'auto',
                      bgcolor: c.features?.[f.key] ? 'success.main' : 'transparent',
                      border: 1, borderColor: c.features?.[f.key] ? 'success.main' : 'divider',
                    }} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }}>Adoption</TableCell>
              {FEATURES.map((f) => (
                <TableCell key={f.key} align="center" sx={{ ...cellSx, fontWeight: 600 }}>
                  <Typography component="span" data-testid={`adoption-${f.key}`} sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    {fmtRate(usedBy[f.key], centers.length)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  );
};

export default AdoptionHeatmap;
